import { Banknote, CreditCard, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PaymentHeader({ isRefund, absTotal, effectiveTotal, ebtTaxAdjustment, tenders, absPaid, remainingBalance, headerFont, headerSize, headerWeight, headerBgColor, headerFontColor, totalFont, totalSize, totalWeight, totalFontColor }) {
  return (
    <div className="px-4 py-3" style={{ backgroundColor: isRefund ? '#7F1D1D' : headerBgColor }}>
      <div className="flex items-center justify-between">
        <h2 style={{
          fontFamily: `${headerFont}, sans-serif`,
          fontSize: `${headerSize}px`,
          fontWeight: headerWeight,
          color: headerFontColor
        }}>{isRefund ? 'Refund' : 'Payment'}</h2>
        <div className="text-right">
          <div className="text-sm" style={{ color: headerFontColor, opacity: 0.7 }}>{isRefund ? 'Refund Amount' : 'Total Due'}</div>
          <div style={{
            fontFamily: `${totalFont}, sans-serif`,
            fontSize: `${totalSize}px`,
            fontWeight: totalWeight,
            color: isRefund ? '#FCA5A5' : totalFontColor
          }}>${isRefund ? absTotal.toFixed(2) : effectiveTotal.toFixed(2)}</div>
          {ebtTaxAdjustment > 0 && (
            <div className="text-[10px] text-green-400">EBT Tax Exempt: -${ebtTaxAdjustment.toFixed(2)}</div>
          )}
        </div>
      </div>
      {tenders.length > 0 && (
        <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${headerFontColor}40` }}>
          <div className="flex justify-between text-sm" style={{ color: headerFontColor }}>
            <span>{isRefund ? 'Refunded' : 'Paid'}: ${absPaid.toFixed(2)}</span>
            <span className="font-bold text-yellow-400">Remaining: ${remainingBalance.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function AppliedTenders({ tenders, removeTender, voidingIdx = null }) {
  if (tenders.length === 0) return null;
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-2 space-y-1" data-testid="applied-tenders">
      <div className="text-xs font-semibold text-green-700 uppercase">Applied Payments</div>
      {tenders.map((tender, idx) => (
        <div key={tender.id ?? `${tender.method}-${idx}`} className="flex items-center justify-between text-sm bg-white rounded px-2 py-1" data-testid={`tender-row-${idx}`}>
          <span className="flex items-center gap-2">
            {tender.type === 'CASH' ? <Banknote className="w-4 h-4 text-green-600" /> :
              <CreditCard className="w-4 h-4 text-blue-600" />}
            <span className="font-medium">{tender.type.replace('_', ' ')}</span>
            {tender.partial && (
              <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" data-testid={`tender-partial-badge-${idx}`}>
                Partial · asked ${Number(tender.requested_amount || 0).toFixed(2)}
              </span>
            )}
          </span>
          <span className="flex items-center gap-2">
            <span className="font-bold text-green-700" data-testid={`tender-amount-${idx}`}>${tender.amount.toFixed(2)}</span>
            {tender.type === 'CASH' ? (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeTender(idx)} data-testid={`tender-remove-${idx}`}>
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] font-semibold text-red-600 hover:text-red-700"
                disabled={voidingIdx !== null} onClick={() => removeTender(idx)} data-testid={`tender-void-${idx}`}>
                {voidingIdx === idx ? "Voiding…" : "Void"}
              </Button>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

export function PaymentResult({ paymentResult, compact = false, remainingBalance = null }) {
  if (!paymentResult) return null;
  if (paymentResult.approved && paymentResult.partial) {
    const taken = Number(paymentResult.approved_amount ?? 0);
    const due = remainingBalance != null ? remainingBalance : Number(paymentResult.amount_due ?? 0);
    return (
      <div className={`${compact ? 'p-3' : 'p-2'} rounded-lg bg-amber-50 border border-amber-300`} data-testid="partial-approval-banner">
        <p className="font-bold text-amber-900 text-sm">PARTIALLY APPROVED</p>
        <p className="text-sm text-amber-900">
          Card charged <span className="font-bold" data-testid="partial-approved-amount">${taken.toFixed(2)}</span>
          {paymentResult.card_type ? ` (${paymentResult.card_type}${paymentResult.last4 ? ` ****${paymentResult.last4}` : ''})` : ''}
          {' · '}<span className="font-bold" data-testid="partial-remaining-due">${due.toFixed(2)} still due</span>
        </p>
        <p className="text-xs text-amber-800">Take the rest with cash, another card or EBT — or Void the card payment to cancel.</p>
      </div>
    );
  }
  return (
    <div className={`${compact ? 'p-3' : 'p-2'} rounded-lg flex items-center gap-2 ${paymentResult.approved ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
      }`}>
      {paymentResult.approved ? (
        <svg className={`${compact ? 'w-6 h-6' : 'w-5 h-5'} text-green-600`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
      ) : (
        <svg className={`${compact ? 'w-6 h-6' : 'w-5 h-5'} text-red-600`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
      )}
      <div className="flex-1">
        <p className={`font-bold ${compact ? '' : 'text-sm'} ${paymentResult.approved ? 'text-green-800' : 'text-red-800'}`}>
          {paymentResult.approved ? 'APPROVED' : 'DECLINED'}
        </p>
        {!compact && !paymentResult.approved && paymentResult.decline_reason ? (
          <div>
            <p className="text-sm font-semibold text-red-700">{paymentResult.decline_reason}</p>
            {paymentResult.decline_code && (
              <p className="text-[10px] text-red-400 font-mono">Code: {paymentResult.decline_code}</p>
            )}
          </div>
        ) : !compact && (
          <p className="text-xs text-zinc-600">{paymentResult.message}</p>
        )}
      </div>
    </div>
  );
}
